/**
 * <zerbay-country-sheet>: controller of the country and language sheet (rows CTRY-05/06/07).
 *
 * Markup, styles and the inline classic opener live in sections/zerbay-country-sheet.liquid; the flag button is
 * snippets/zerbay-country-button.liquid. Contract with the header: country/contract.md (sections 4 to 9):
 * - Opening: any [data-zb-country-open] is handled by the inline opener, which calls showDialog(opener) once this
 *   element has upgraded (zbReady). If the sheet was opened before the upgrade, connectedCallback adopts it.
 * - One layer at a time: document events zb:layer-open / zb:layer-close with detail.layer.
 * - While open: html.zb-scroll-lock plus Horizon's lockScroll(); aria-expanded kept on every opener.
 * - Closing: Esc / cancel, native close (Android back), backdrop tap, X, current-language pill, swipe down on the
 *   handle or head below 960 px, crossing 960 px, bfcache restore, section unload.
 *
 * Why showDialog and closeDialog are re-implemented instead of calling DialogComponent's:
 * closeDialog is an arrow-function class field in dialog.js, so a subclass method of that name would be shadowed by
 * the instance field and super.closeDialog does not exist. The base's private #previousScrollY is set only by the
 * base showDialog, so closing a dialog the inline opener already opened would scroll the page to the top. The base
 * showDialog also opens inside requestAnimationFrame, a frame later than the inline opener. So showDialog is
 * overridden as a prototype method (it never calls super.showDialog) and closeDialog is replaced by a class field of
 * the same name (subclass fields initialise after the base's, so it wins; toggleDialog reaches it too). Horizon's
 * lockScroll / unlockScroll, the dialog:open / dialog:close events, refs and the element lifecycle are reused.
 */
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { lockScroll, unlockScroll, isClickedOutside, prefersReducedMotion } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const LAYER = 'country-sheet';
const DESKTOP_QUERY = '(min-width: 960px)';
const OPENER_SELECTOR = '[data-zb-country-open]';
const CLOSE_ANIMATION_FALLBACK_MS = 320;

/**
 * @typedef {object} ZerbayCountrySheetRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [closeButton]
 * @property {HTMLElement} [body]
 */

/** @extends {DialogComponent} */
class ZerbayCountrySheet extends DialogComponent {
  #open = false;
  /** The one pending close (animated, instant or swipe-out), shared by every close path; null when none. */
  /** @type {null | ((options?: { noFocus?: boolean }) => void)} */
  #finish = null;
  /** @type {HTMLElement | null} */
  #opener = null;
  #scrollTop = 0;
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {{ x: number, y: number, dragging: boolean } | null} */
  #drag = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #snapTimer;
  /** Kept in a field: a MediaQueryList with only a listener can be garbage-collected and stop firing. */
  #desktopQuery = window.matchMedia(DESKTOP_QUERY);

  /** True once the element has upgraded; the inline opener reads it. */
  zbReady = false;

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    dialog.addEventListener('cancel', this.#onCancel, { signal });
    dialog.addEventListener('close', this.#onNativeClose, { signal });
    dialog.addEventListener('click', this.#onDialogClick, { signal });

    for (const grip of dialog.querySelectorAll('[data-zb-cs-grip]')) {
      grip.addEventListener('touchstart', this.#onTouchStart, { passive: true, signal });
      grip.addEventListener('touchmove', this.#onTouchMove, { passive: true, signal });
      grip.addEventListener('touchend', this.#onTouchEnd, { passive: true, signal });
      grip.addEventListener('touchcancel', this.#onTouchCancel, { passive: true, signal });
    }

    // Crossing 960 px swaps sheet and side panel, so close without animation and without moving focus.
    this.#desktopQuery.addEventListener('change', this.#closeSilently, { signal });
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) this.#closeSilently();
      },
      { signal }
    );
    document.addEventListener(
      'zb:layer-open',
      (event) => {
        const layer = /** @type {CustomEvent} */ (event).detail?.layer;
        if (layer !== LAYER) this.#closeSilently();
      },
      { signal }
    );

    this.zbReady = true;

    // Adopt a sheet the inline opener opened before this element upgraded (no re-centring, no focus move).
    // A sheet that is already closed but still marked 'early' is left to the inline clean-up.
    const early = /** @type {any} */ (dialog);
    if (dialog.hasAttribute('open') && early.zbOwner === 'early') {
      early.zbOwner = 'module';
      this.#open = true;
      this.#opener = early.zbOpener ?? null;
      this.#scrollTop = getScrollTop();
      lockScroll(dialog);
      document.documentElement.classList.add('zb-scroll-lock');
      this.#setExpanded(true);
    }
  }

  disconnectedCallback() {
    this.zbReady = false;
    if (this.#finish) {
      this.#finish({ noFocus: true });
    } else if (this.#open) {
      this.closeDialog({ instant: true, noFocus: true });
    }
    this.#abort?.abort();
    this.#abort = null;
    super.disconnectedCallback();
  }

  /**
   * Opens the sheet synchronously (same timing as the inline opener).
   * @param {Element | null} [opener]
   */
  showDialog(opener) {
    const { dialog, closeButton } = this.refs;
    if (dialog.hasAttribute('open') || this.#finish) return;

    // The header closes its own layers at once; Horizon's docked chat drawer is closed here.
    document.dispatchEvent(new CustomEvent('zb:layer-open', { detail: { layer: LAYER } }));
    const chat = /** @type {any} */ (document.getElementById('chat-drawer'));
    if (chat && chat.isOpen && typeof chat.close === 'function') chat.close();

    this.#teardownGesture();
    this.#opener = opener instanceof HTMLElement ? opener : null;
    this.#scrollTop = getScrollTop();

    try {
      dialog.showModal();
    } catch {
      // No showModal: the inline script handles Esc, Tab and outside taps in this mode.
      dialog.setAttribute('open', '');
      dialog.classList.add('zb-cs--fallback');
    }

    /** @type {any} */ (dialog).zbOwner = 'module';
    this.#open = true;
    lockScroll(dialog);
    document.documentElement.classList.add('zb-scroll-lock');
    this.#setExpanded(true);
    this.#centreCurrent();
    closeButton?.focus({ preventScroll: true });
    this.dispatchEvent(new DialogOpenEvent());
  }

  /**
   * Closes the sheet. Replaces the base class field of the same name (see the header comment).
   * While a close is pending, a non-instant request is ignored and an instant one settles it now.
   * @param {{ instant?: boolean, noFocus?: boolean }} [options]
   */
  closeDialog = ({ instant = false, noFocus = false } = {}) => {
    if (this.#finish) {
      if (instant) this.#finish({ noFocus });
      return;
    }
    const { dialog } = this.refs;
    if (!dialog.hasAttribute('open')) return;

    if (instant || prefersReducedMotion()) {
      this.#beginClose(noFocus, (finish) => finish());
      return;
    }
    this.#beginClose(noFocus, (finish, setTimer, setOnEnd) => {
      // Restart the animation so the closing class always plays from its first frame.
      dialog.style.animation = 'none';
      void dialog.offsetWidth;
      dialog.style.animation = '';
      dialog.classList.add('zb-cs--closing');
      setOnEnd((/** @type {AnimationEvent} */ event) => {
        if (event.target === dialog && !event.pseudoElement) finish();
      });
      setTimer(setTimeout(() => finish(), CLOSE_ANIMATION_FALLBACK_MS));
    });
  };

  /**
   * Creates the single pending close and lets `run` start its animation or timer. The finisher is idempotent:
   * whichever of the animation end, the safety timer or an instant request fires first settles the close.
   * @param {boolean} noFocus
   * @param {(finish: (options?: { noFocus?: boolean }) => void, setTimer: (t: ReturnType<typeof setTimeout>) => void, setOnEnd: (fn: (event: AnimationEvent) => void) => void) => void} run
   */
  #beginClose(noFocus, run) {
    const { dialog } = this.refs;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timer;
    /** @type {((event: AnimationEvent) => void) | null} */
    let onEnd = null;

    /** @type {(options?: { noFocus?: boolean }) => void} */
    const finish = ({ noFocus: later = false } = {}) => {
      if (this.#finish !== finish) return;
      this.#finish = null;
      clearTimeout(timer);
      if (onEnd) dialog.removeEventListener('animationend', onEnd);
      this.#teardownGesture();
      dialog.classList.remove('zb-cs--closing');
      // Relies on the dialog's close event being queued (HTML spec): #onNativeClose cannot run before #cleanup.
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      this.#cleanup({ noFocus: noFocus || later });
    };

    this.#finish = finish;
    run(
      finish,
      (t) => {
        timer = t;
      },
      (fn) => {
        onEnd = fn;
        dialog.addEventListener('animationend', fn);
      }
    );
  }

  /**
   * Shared clean-up for every close path; idempotent.
   * @param {{ noFocus?: boolean }} options
   */
  #cleanup({ noFocus = false } = {}) {
    if (!this.#open) return;
    const { dialog } = this.refs;

    this.#open = false;
    /** @type {any} */ (dialog).zbOwner = null;
    unlockScroll(dialog);

    let otherModal = null;
    try {
      otherModal = document.querySelector('dialog:modal');
    } catch {
      otherModal = null;
    }
    if (!otherModal) document.documentElement.classList.remove('zb-scroll-lock');
    dialog.classList.remove('zb-cs--fallback');
    this.#setExpanded(false);

    // Restore the page position only if it moved.
    if (getScrollTop() !== this.#scrollTop) scrollTo({ top: this.#scrollTop, behavior: 'instant' });

    this.dispatchEvent(new DialogCloseEvent());
    document.dispatchEvent(new CustomEvent('zb:layer-close', { detail: { layer: LAYER } }));

    if (!noFocus) {
      const openers = [...document.querySelectorAll(OPENER_SELECTOR)];
      const target =
        this.#opener && this.#opener.getClientRects().length
          ? this.#opener
          : openers.find((el) => el.getClientRects().length);
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }
    this.#opener = null;
  }

  #closeSilently = () => {
    if (this.#open) this.closeDialog({ instant: true, noFocus: true });
  };

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    for (const el of document.querySelectorAll(OPENER_SELECTOR)) {
      el.setAttribute('aria-expanded', String(expanded));
    }
  }

  /** Scrolls the list so the current country sits in the middle (on every open). */
  #centreCurrent() {
    const { body } = this.refs;
    const current = /** @type {HTMLElement | null} */ (body?.querySelector('[aria-current="true"]'));
    if (!body || !current) return;
    body.scrollTop = Math.max(0, current.offsetTop - (body.clientHeight - current.offsetHeight) / 2);
  }

  /** Esc on the modal dialog and Android back (CloseWatcher): close with the animation. */
  #onCancel = (/** @type {Event} */ event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /** Native close from anywhere else (for example other code calling dialog.close()). */
  #onNativeClose = () => {
    // A queued close event can arrive after a newer opening: the newer opening owns the state.
    if (this.refs.dialog.hasAttribute('open')) return;
    if (this.#finish) {
      this.#finish();
    } else if (this.#open) {
      this.#teardownGesture();
      this.refs.dialog.classList.remove('zb-cs--closing');
      this.#cleanup({ noFocus: false });
    }
  };

  /** Bound on the dialog itself because iOS does not deliver backdrop taps to the document. */
  #onDialogClick = (/** @type {MouseEvent} */ event) => {
    const { dialog } = this.refs;
    const target = event.target;
    if (target instanceof Element && target.closest('[data-zb-sheet-close]')) {
      event.preventDefault();
      this.closeDialog();
      return;
    }
    if (target === dialog && isClickedOutside(event, dialog)) this.closeDialog();
  };

  // Swipe down on the handle or head to close (phones and tablets only; never on the list).
  // While a close is pending (#finish set) touch input is ignored.

  #onTouchStart = (/** @type {TouchEvent} */ event) => {
    if (!this.#open || this.#finish) return;
    if (this.#desktopQuery.matches || event.touches.length !== 1) return;
    // A re-grab within the snap-back keeps the current transform: only the timer that would clear it is dropped.
    clearTimeout(this.#snapTimer);
    this.#drag = { x: event.touches[0].clientX, y: event.touches[0].clientY, dragging: false };
  };

  #onTouchMove = (/** @type {TouchEvent} */ event) => {
    const drag = this.#drag;
    if (!drag || this.#finish) return;
    if (event.touches.length > 1) {
      this.#teardownGesture();
      return;
    }
    const dx = event.touches[0].clientX - drag.x;
    const dy = event.touches[0].clientY - drag.y;
    if (!drag.dragging && dy > 6 && dy > Math.abs(dx)) drag.dragging = true;
    if (drag.dragging && !prefersReducedMotion()) {
      const { style } = this.refs.dialog;
      style.transition = 'none';
      style.transform = `translateY(${Math.max(0, dy)}px)`;
    }
  };

  #onTouchEnd = (/** @type {TouchEvent} */ event) => {
    const drag = this.#drag;
    if (!drag || this.#finish) return;
    const touch = event.changedTouches[0];
    const dx = touch ? touch.clientX - drag.x : 0;
    const dy = touch ? touch.clientY - drag.y : 0;
    this.#drag = null;

    if (!(drag.dragging && dy > 60 && dy > Math.abs(dx))) {
      this.#snapBack(drag.dragging);
      return;
    }

    clearTimeout(this.#snapTimer);
    if (prefersReducedMotion()) {
      this.#beginClose(false, (finish) => finish());
      return;
    }
    // Slide out, then settle through the same finisher as every other close.
    this.#beginClose(false, (finish, setTimer) => {
      const { style } = this.refs.dialog;
      style.transition = 'transform 0.18s ease-out';
      style.transform = 'translateY(100%)';
      setTimer(setTimeout(() => finish(), 190));
    });
  };

  #onTouchCancel = () => {
    const drag = this.#drag;
    if (drag && !this.#finish) this.#snapBack(drag.dragging);
  };

  /** @param {boolean} wasDragging */
  #snapBack(wasDragging) {
    this.#drag = null;
    clearTimeout(this.#snapTimer);
    // A plain tap on the head never moved the sheet: nothing to animate.
    if (!wasDragging || prefersReducedMotion()) {
      this.#clearInline();
      return;
    }
    const { style } = this.refs.dialog;
    style.transition = 'transform 0.18s ease-out';
    style.transform = 'translateY(0)';
    this.#snapTimer = setTimeout(() => this.#clearInline(), 200);
  }

  #clearInline() {
    const { style } = this.refs.dialog;
    style.transition = '';
    style.transform = '';
  }

  /** Clears gesture state, the snap timer and the inline transition and transform. */
  #teardownGesture() {
    this.#drag = null;
    clearTimeout(this.#snapTimer);
    this.#snapTimer = undefined;
    if (this.refs?.dialog) this.#clearInline();
  }
}

if (!customElements.get('zerbay-country-sheet')) customElements.define('zerbay-country-sheet', ZerbayCountrySheet);
